import React from 'react';
import { Input, Switcher, Select, DatePicker } from 'components/ui';
import { FormNumericInput } from 'components/shared';

const CustomerCustomField = ({
    field,
    value,
    setFieldValue,
    handleChange,
    errors,
    touched
}) => {
    if (!field || !field.type) return null;

    const fieldName = `customFields.${field.key}`;
    const fieldType = field.type.toLowerCase();
    const config = field.configuration || {};

    switch (fieldType) {
        case 'text':
            return (
                <Input
                    type="text"
                    autoComplete="off"
                    name={fieldName}
                    placeholder={field.label}
                    value={value ?? ''}
                    onChange={(e) => setFieldValue(fieldName, e.target.value)}
                />
            );

        case 'integer':
            return (
                <Input
                    type="number"
                    autoComplete="off"
                    name={fieldName}
                    placeholder={field.label}
                    value={value ?? ''}
                    onChange={(e) => {
                        const val = e.target.value;
                        setFieldValue(fieldName, val === '' ? null : parseInt(val, 10));
                    }}
                />
            );

        case 'decimal':
            return (
                <FormNumericInput
                    name={fieldName}
                    placeholder={field.label}
                    value={value ?? ''}
                    decimalScale={config.decimalPlaces ?? 2}
                    fixedDecimalScale={false}
                    className="w-full text-right"
                    size="md"
                    onChange={(e) => {
                        const val = e.target.value;
                        setFieldValue(fieldName, val);
                    }}
                />
            );

        case 'boolean':
            return (
                <Switcher
                    name={fieldName}
                    checked={Boolean(value)}
                    onChange={(checked) => setFieldValue(fieldName, checked)}
                />
            );

        case 'date':
            return (
                <DatePicker
                    name={fieldName}
                    placeholder={field.label}
                    value={value ? new Date(value) : null}
                    onChange={(date) => setFieldValue(fieldName, date ? date.toISOString().split('T')[0] : null)}
                />
            );

        case 'datetime':
            return (
                <DatePicker
                    name={fieldName}
                    placeholder={field.label}
                    value={value ? new Date(value) : null}
                    onChange={(date) => setFieldValue(fieldName, date ? date.toISOString() : null)}
                />
            );

        case 'select':
            const selectOptions = (config.options || []).map((opt) =>
                typeof opt === 'string'
                    ? { label: opt, value: opt }
                    : { label: opt.label || opt.value, value: opt.value }
            );

            const selectedOption = selectOptions.find(
                (opt) => opt.value === value || opt.value === value?.value
            );

            return (
                <Select
                    name={fieldName}
                    placeholder={field.label}
                    isClearable
                    options={selectOptions}
                    value={selectedOption || null}
                    onChange={(opt) => setFieldValue(fieldName, opt?.value ?? null)}
                />
            );

        case 'multiselect':
            const multiOptions = (config.options || []).map((opt) =>
                typeof opt === 'string'
                    ? { label: opt, value: opt }
                    : { label: opt.label || opt.value, value: opt.value }
            );

            const currentArray = Array.isArray(value) ? value : [];
            const selectedValues = multiOptions.filter((opt) => currentArray.includes(opt.value));

            return (
                <Select
                    isMulti
                    name={fieldName}
                    placeholder={field.label}
                    isClearable
                    options={multiOptions}
                    value={selectedValues}
                    onChange={(opts) => {
                        const vals = opts ? opts.map((o) => o.value) : [];
                        setFieldValue(fieldName, vals);
                    }}
                />
            );

        default:
            return (
                <Input
                    type="text"
                    autoComplete="off"
                    name={fieldName}
                    placeholder={field.label}
                    value={value ?? ''}
                    onChange={(e) => setFieldValue(fieldName, e.target.value)}
                />
            );
    }
};

export default CustomerCustomField;
